import { api } from './client';

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

/**
 * Відповідь POST /auth/login (Platform Admin Блок A — 2FA).
 *
 * Якщо у user'а увімкнено TOTP (платформенний власник) — backend повертає
 * `requires_2fa=true` + одноразовий `mfa_token` (без access/refresh). Frontend
 * показує другий екран вводу 6-значного коду і викликає POST /auth/login/2fa.
 *
 * Якщо 2FA не потрібна — звичайна відповідь з `access_token`/`refresh_token`
 * (як TokenPair), `requires_2fa=false`, `mfa_token=null`.
 */
export interface LoginResult {
  requires_2fa: boolean;
  mfa_token: string | null;
  access_token: string | null;
  refresh_token: string | null;
}

/** GET /auth/platform-status — чи user платформенний власник + стан TOTP. */
export interface PlatformStatus {
  is_platform_owner: boolean;
  totp_enabled: boolean;
}

/** POST /auth/2fa/setup — секрет + otpauth:// URI для QR. */
export interface TotpSetupResult {
  secret: string;
  provisioning_uri: string;
}

/**
 * Відповідь GET /auth/me (з Порції 3.0.5.0 — tenant-scoped).
 *
 * Endpoint потребує `X-Tenant-Id`; повертає user-профіль + tenant_role +
 * агрегований список permissions для поточного tenant'а.
 */
export interface MeResponse {
  id: string;
  full_name: string;
  email: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  tenant_role: 'OWNER' | 'ADMIN' | 'MEMBER';
  permissions: string[];
  /**
   * Per-org permissions map (UX α 2026-05-21). Frontend читає для
   * OrgSelector-фільтра у формах ПКО/Реалізації/тощо — щоб user
   * бачив у dropdown'і лише ті org, де реально має потрібний permission.
   * Семантика: { "<organization_id>": ["perm.code.a", "perm.code.b", ...] }.
   * • OWNER/ADMIN — усі org tenant'а з повним набором.
   * • MEMBER — лише org з user_access, з permissions per-org.
   * • UNION values == set(permissions) (інваріант з backend).
   *
   * Поле optional на frontend-стороні щоб уникнути синхронного зрушення
   * усіх mock'ів у тестах. Споживачі (usePermissionsByOrg) роблять `?? {}`.
   */
  permissions_by_org?: Record<string, string[]>;

  /**
   * Коди access-ролей user'а у поточному tenant'і (Порція Роздріб.6).
   *
   * Призначення: frontend POS-redirect логіка. Якщо user має тільки
   * 'CASHIER_POS' (без 'PROJECT_ADMIN' / 'CHIEF_ACCOUNTANT' тощо) —
   * SelectTenantPage авто-редіректить на /pos.
   *
   * • OWNER/ADMIN — завжди [] (немає user_access — це tenant-рівнева роль).
   * • MEMBER     — DISTINCT по ar.code з усіх ACTIVE user_access.
   * Поле optional для backward compat зі старими тестовими mock'ами.
   */
  access_role_codes?: string[];
}

/** Елемент списку GET /auth/my-tenants. */
export interface MyTenantItem {
  id: string;
  name: string;
  slug: string | null;
  tenant_role: 'OWNER' | 'ADMIN' | 'MEMBER';
  display_role: string;
}

/**
 * POST /auth/login (Порція 3.0.8: API field 'login' → 'email').
 * Параметр функції зберігаємо як `loginValue` для backward compat у
 * викликачах; на бекенд шлемо як `email`.
 */
export async function login(loginValue: string, password: string): Promise<LoginResult> {
  const { data } = await api.post<LoginResult>('/auth/login', {
    email: loginValue,
    password,
  });
  return data;
}

/**
 * Другий крок 2FA-логіну. Приймає одноразовий `mfa_token` з першого кроку
 * + 6-значний TOTP-код з Google Authenticator. Повертає звичайну пару токенів.
 */
export async function login2fa(mfaToken: string, code: string): Promise<TokenPair> {
  const { data } = await api.post<TokenPair>('/auth/login/2fa', {
    mfa_token: mfaToken,
    code,
  });
  return data;
}

/** Стан платформенного доступу + 2FA поточного user'а. */
export async function getPlatformStatus(): Promise<PlatformStatus> {
  const { data } = await api.get<PlatformStatus>('/auth/platform-status');
  return data;
}

/** Ініціалізація 2FA: генерує секрет + otpauth:// URI (НЕ вмикає ще). */
export async function totpSetup(): Promise<TotpSetupResult> {
  const { data } = await api.post<TotpSetupResult>('/auth/2fa/setup');
  return data;
}

/** Підтвердження першого коду → вмикає 2FA (204 No Content). */
export async function totpEnable(code: string): Promise<void> {
  await api.post('/auth/2fa/enable', { code });
}

export interface SignupRequest {
  email: string;
  password: string;
  full_name: string;
  tenant_name: string;
}

export async function signup(payload: SignupRequest): Promise<TokenPair> {
  const { data } = await api.post<TokenPair>('/auth/signup', payload);
  return data;
}

export async function logout(refreshToken: string): Promise<void> {
  await api.post('/auth/logout', { refresh_token: refreshToken });
}

export async function getMe(): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>('/auth/me');
  return data;
}

/** Список tenant'ів, де user має ACTIVE membership. Глобальний endpoint
 *  (X-Tenant-Id ігнорується). */
export async function getMyTenants(): Promise<MyTenantItem[]> {
  const { data } = await api.get<MyTenantItem[]>('/auth/my-tenants');
  return data;
}

export interface CreateTenantRequest {
  tenant_name: string;
  billing_email?: string;
}

export interface CreateTenantResponse {
  id: string;
  name: string;
  slug: string | null;
}

/** Створює новий tenant для залогіненого user'а (Порція 3.0.5.0). */
export async function createTenant(
  payload: CreateTenantRequest,
): Promise<CreateTenantResponse> {
  const { data } = await api.post<CreateTenantResponse>(
    '/auth/create-tenant',
    payload,
  );
  return data;
}
